// Single source of truth for page-level SEO metadata (title, description, URL).
//
// This file is pure JavaScript with no Vite-only or browser-only APIs, so it is
// imported by BOTH:
//   - src/components/common/RouteSeo.jsx  (updates <head> after client-side navigation)
//   - scripts/postbuild-seo.mjs           (writes per-route HTML files and sitemap.xml at build time)
// Keeping the rules here means the browser and the build can never disagree.

export const SITE_URL = 'https://yacc.tech';

export const DEFAULT_TITLE = 'YACC | Yet Another Coding Club, IIT Palakkad (IITPKD)';

export const DEFAULT_DESCRIPTION =
  'Yet Another Coding Club (YACC) is the official coding club of IIT Palakkad (IITPKD), covering competitive programming, web, app and game development, and more.';

const TITLE_SUFFIX = ' | YACC - IIT Palakkad';

export const NOT_FOUND_TITLE = `Page Not Found${TITLE_SUFFIX}`;

export function pageTitle(title) {
  return `${title}${TITLE_SUFFIX}`;
}

export function truncate(text, max = 160) {
  const clean = String(text || '').replace(/\s+/g, ' ').trim();
  if (clean.length <= max) return clean;
  return `${clean.slice(0, max - 1).replace(/\s+\S*$/, '')}…`;
}

/** "/events/" -> "/events", "" -> "/" */
export function normalizePath(pathname) {
  const trimmed = String(pathname || '').replace(/\/+$/, '');
  return trimmed === '' ? '/' : trimmed;
}

export function absoluteUrl(path) {
  const p = normalizePath(path);
  return p === '/' ? `${SITE_URL}/` : `${SITE_URL}${p}`;
}

const SECTION_PAGES = [
  {
    path: '/disciplines',
    heading: 'Disciplines',
    description:
      'The six disciplines of YACC, the coding club of IIT Palakkad (IITPKD): competitive programming, functional programming, game, web and app development, and system design.',
  },
  {
    path: '/blog',
    heading: 'Blog',
    description:
      'Articles, tutorials and write-ups from YACC, the official coding club of IIT Palakkad (IITPKD).',
  },
  {
    path: '/projects',
    heading: 'Projects',
    description:
      'Student projects and open-source work built by members of YACC, the coding club of IIT Palakkad (IITPKD).',
  },
  {
    path: '/events',
    heading: 'Events',
    description:
      'Workshops, contests, bootcamps and hackathons organised by YACC, the coding club of IIT Palakkad (IITPKD).',
  },
  {
    path: '/people',
    heading: 'People',
    description:
      'The core team, members and alumni of YACC, the coding club of IIT Palakkad (IITPKD).',
  },
];

/**
 * Builds the complete list of indexable routes from content data.
 * @returns {{path:string, heading:string, title:string, description:string, lastmod?:string}[]}
 */
export function buildRoutes({ disciplines = [], posts = [], events = [], projects = [] } = {}) {
  const routes = [
    {
      path: '/',
      heading: 'Yet Another Coding Club (YACC)',
      title: DEFAULT_TITLE,
      description: DEFAULT_DESCRIPTION,
    },
    ...SECTION_PAGES.map((s) => ({ ...s, title: pageTitle(s.heading) })),
  ];

  for (const d of disciplines) {
    routes.push({
      path: `/disciplines/${d.slug}`,
      heading: d.name,
      title: pageTitle(d.name),
      description: truncate(`${d.name} at YACC, the coding club of IIT Palakkad. ${d.tagline || ''}`),
    });
  }

  for (const post of posts) {
    routes.push({
      path: `/blog/${post.slug}`,
      heading: post.title,
      title: pageTitle(post.title),
      description: truncate(
        post.summary || `${post.title}, an article by ${post.author} on the YACC blog, IIT Palakkad.`,
      ),
      lastmod: post.lastEditedDate || post.publishedDate || undefined,
    });
  }

  for (const event of events) {
    routes.push({
      path: `/events/${event.slug}`,
      heading: event.title,
      title: pageTitle(event.title),
      description: truncate(event.description || `${event.title}, an event by YACC, IIT Palakkad.`),
    });
  }

  for (const project of projects) {
    routes.push({
      path: `/projects/${project.slug}`,
      heading: project.title,
      title: pageTitle(project.title),
      description: truncate(
        project.summary || `${project.title}, a project by YACC members at IIT Palakkad.`,
      ),
    });
  }

  return routes;
}

export function findRoute(routes, pathname) {
  const target = normalizePath(pathname);
  const direct = routes.find((r) => r.path === target);
  if (direct) return direct;

  if (target.startsWith('/blog/')) {
    const slug = target.slice('/blog/'.length);
    const cleanSlug = slug.replace(/^\d{4}-\d{2}-\d{2}-/, '');
    return routes.find((r) => {
      if (!r.path.startsWith('/blog/')) return false;
      const rSlug = r.path.slice('/blog/'.length);
      const rCleanSlug = rSlug.replace(/^\d{4}-\d{2}-\d{2}-/, '');
      return rCleanSlug === cleanSlug;
    });
  }

  return undefined;
}
